// src/components/admin/TorreDeControl/modals/PostponeModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Calendar, Package as PackageIcon, Truck, Warehouse } from 'lucide-react';
import axios from 'axios';
import DateSelector from '../DateSelector';

const API_URL = process.env.REACT_APP_API_URL;

function PostponeModal({ item, onClose, onUpdate }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [packageItems, setPackageItems] = useState([]);
  const [isFetchingItems, setIsFetchingItems] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [postponeLocation, setPostponeLocation] = useState('in_fastmax'); // 'in_fastmax' or 'with_driver'

  useEffect(() => {
    fetchPackageItems();
  }, [item.id]);

  async function fetchPackageItems() {
    setIsFetchingItems(true);
    setFetchError(null);

    try {
      const response = await axios.get(
        `${API_URL}/api/admin/delivery-point/${item.id}/package-items`
      );
      setPackageItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching package items:', error);
      setFetchError('No se pudieron cargar los productos del paquete');
    } finally {
      setIsFetchingItems(false);
    }
  }

  function formatDateForDisplay(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!selectedDate) {
      alert('Por favor selecciona una fecha');
      return;
    }

    if (packageItems.length === 0) {
      alert('No se encontraron productos para esta entrega');
      return;
    }

    // Validate driver assignment if postponing with driver
    if (postponeLocation === 'with_driver' && !item.driver_id) {
      alert('No se puede posponer "con motorizado" sin motorizado asignado');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/entrega/postpone/${item.id}`,
        {
          delivery_date: selectedDate,
          postpone_location: postponeLocation
        }
      );

      const newDate = formatDateForDisplay(selectedDate);
      const locationText = postponeLocation === 'in_fastmax' 
        ? 'en FastMax' 
        : 'con motorizado';
      
      alert(
        `Entrega pospuesta exitosamente para el ${newDate}\n` +
        `Ubicación: ${locationText}\n` +
        `Código de seguimiento: ${response.data.tracking_code}`
      );
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error postponing delivery:', error);
      const errorMsg = error.response?.data?.error || 'Error al posponer entrega';
      alert(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }

  // Determine if delivery can be postponed with driver
  // Can postpone with driver if: has driver AND status is valid (not delivered/rejected/canceled/already postponed)
  const canPostponeWithDriver = item.driver_id && 
    !['entregado', 'rechazado', 'cancelado', 'postergado'].includes(item.status);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        <div className="bg-brand-primary px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg flex items-center gap-2 font-montserrat">
            <Calendar className="w-5 h-5" />
            Posponer Entrega
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Current Delivery Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 font-montserrat">
              <strong>Entrega actual:</strong> #{item.id}
            </p>
            <p className="text-sm text-blue-800 font-montserrat mt-1">
              <strong>Fecha actual:</strong>{' '}
              {item.delivery_date
                ? new Date(item.delivery_date).toLocaleDateString('es-PE')
                : 'N/A'}
            </p>
            <p className="text-sm text-blue-800 font-montserrat mt-1">
              <strong>Estado:</strong> {item.status}
            </p>
            <p className="text-sm text-blue-800 font-montserrat mt-1">
              <strong>Destinatario:</strong> {item.recipient_name}
            </p>
            {item.district && (
              <p className="text-sm text-blue-800 font-montserrat mt-1">
                <strong>Distrito:</strong> {item.district}
              </p>
            )}
            {item.driver_id && (
              <p className="text-sm text-blue-800 font-montserrat mt-1">
                <strong>Motorizado asignado:</strong> ID {item.driver_id}
              </p>
            )}
          </div>

          {/* Package Items Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <PackageIcon className="w-4 h-4 text-slate-600" />
              <h4 className="text-sm font-bold text-slate-800 font-montserrat">
                Productos a posponer
              </h4>
            </div>
            
            {isFetchingItems ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary"></div>
                <span className="ml-2 text-sm text-slate-600 font-montserrat">
                  Cargando productos...
                </span>
              </div>
            ) : fetchError ? (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-sm text-red-800 font-montserrat">{fetchError}</p>
              </div>
            ) : packageItems.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm text-yellow-800 font-montserrat">
                  No se encontraron productos para esta entrega
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {packageItems.map((pkgItem, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center text-sm bg-white rounded border border-slate-200 p-2"
                  >
                    <span className="text-slate-700 font-montserrat">
                      SKU: {pkgItem.sku}
                    </span>
                    <span className="text-slate-600 font-montserrat">
                      Cantidad: {pkgItem.quantity}
                    </span>
                  </div>
                ))}
                <p className="text-xs text-slate-500 font-montserrat mt-2">
                  Total: {packageItems.reduce((sum, pkgItem) => sum + pkgItem.quantity, 0)} unidad(es)
                </p>
              </div>
            )}
          </div>

          {/* Postpone Location Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3 font-montserrat">
              Ubicación actual del paquete
            </label>
            <div className="space-y-2">
              {/* In FastMax Option */}
              <label
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  postponeLocation === 'in_fastmax'
                    ? 'border-brand-primary bg-pink-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="postponeLocation"
                  value="in_fastmax"
                  checked={postponeLocation === 'in_fastmax'}
                  onChange={(e) => setPostponeLocation(e.target.value)}
                  className="w-4 h-4 text-brand-primary focus:ring-brand-primary"
                />
                <Warehouse className={`w-5 h-5 ${
                  postponeLocation === 'in_fastmax' ? 'text-brand-primary' : 'text-slate-400'
                }`} />
                <div className="flex-1">
                  <p className={`font-medium font-montserrat ${
                    postponeLocation === 'in_fastmax' ? 'text-brand-primary' : 'text-slate-700'
                  }`}>
                    En FastMax
                  </p>
                  <p className="text-xs text-slate-500 font-montserrat">
                    El paquete está en nuestras instalaciones
                  </p>
                </div>
              </label>

              {/* With Driver Option */}
              <label
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  !canPostponeWithDriver
                    ? 'opacity-50 cursor-not-allowed'
                    : postponeLocation === 'with_driver'
                    ? 'border-brand-primary bg-pink-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="postponeLocation"
                  value="with_driver"
                  checked={postponeLocation === 'with_driver'}
                  onChange={(e) => setPostponeLocation(e.target.value)}
                  disabled={!canPostponeWithDriver}
                  className="w-4 h-4 text-brand-primary focus:ring-brand-primary disabled:cursor-not-allowed"
                />
                <Truck className={`w-5 h-5 ${
                  postponeLocation === 'with_driver' 
                    ? 'text-brand-primary' 
                    : canPostponeWithDriver 
                    ? 'text-slate-400' 
                    : 'text-slate-300'
                }`} />
                <div className="flex-1">
                  <p className={`font-medium font-montserrat ${
                    postponeLocation === 'with_driver' 
                      ? 'text-brand-primary' 
                      : canPostponeWithDriver 
                      ? 'text-slate-700' 
                      : 'text-slate-400'
                  }`}>
                    Con motorizado
                  </p>
                  <p className="text-xs text-slate-500 font-montserrat">
                    {canPostponeWithDriver
                      ? 'El motorizado mantiene el paquete'
                      : 'No disponible (sin motorizado asignado o estado inválido)'}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Date Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Nueva fecha de entrega
            </label>
            <DateSelector
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              showCalendar={showCalendar}
              onToggleCalendar={() => setShowCalendar(!showCalendar)}
            />
          </div>

          {/* Info Note */}
          <div className={`border rounded-lg p-4 ${
            postponeLocation === 'in_fastmax' 
              ? 'bg-blue-50 border-blue-200' 
              : 'bg-purple-50 border-purple-200'
          }`}>
            <p className={`text-xs font-montserrat ${
              postponeLocation === 'in_fastmax' ? 'text-blue-800' : 'text-purple-800'
            }`}>
              <strong>Información:</strong>
            </p>
            <ul className={`text-xs mt-2 space-y-1 font-montserrat ${
              postponeLocation === 'in_fastmax' ? 'text-blue-700' : 'text-purple-700'
            }`}>
              <li>• Se creará nueva entrega para {formatDateForDisplay(selectedDate) || 'la fecha seleccionada'}</li>
              <li>• El código de seguimiento se mantiene igual</li>
              <li>• La entrega actual se marcará como "postergada"</li>
              {postponeLocation === 'with_driver' ? (
                <li>• El motorizado mantiene el paquete hasta nueva entrega</li>
              ) : (
                <li>• El paquete permanece en FastMax hasta nueva asignación</li>
              )}
              {item.driver_id && (
                <li>• Motorizado ID {item.driver_id} mantiene asignación</li>
              )}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedDate || packageItems.length === 0 || isFetchingItems}
              className="flex-1 bg-brand-primary hover:bg-brand-primary/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              {isLoading ? 'Posponiendo...' : 'Posponer Entrega'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PostponeModal;